import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Info, AlertTriangle } from 'lucide-react';

export default function SeaCloudMuteInstall() {
  useEffect(() => {
    document.title = 'Installing SeaCloud Mute | SeaCloud Software';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="product-page">
      <div className="product-hero">
        <div className="section-inner product-hero-inner">
          <Link to="/seacloud-mute" className="legal-back product-back">← Back to SeaCloud Mute</Link>
          <h1 className="product-hero-title">Installing SeaCloud Mute</h1>
          <p className="product-hero-tagline">
            Five minutes, once. Most of it is one unusual step macOS makes you take.
          </p>
        </div>
      </div>

      <div className="section-inner">
        <section className="product-install">
          <h2 className="product-section-title">1 · Download and move to Applications</h2>
          <ol className="product-steps">
            <li>
              <span className="product-step-num">1</span>
              <span>Download the zip from the <Link to="/seacloud-mute" className="legal-link">product page</Link>. Your browser will unzip it, usually into <strong>Downloads</strong>.</span>
            </li>
            <li>
              <span className="product-step-num">2</span>
              <span>Drag <strong>SeaCloud Mute.app</strong> into your <strong>Applications</strong> folder. This matters — macOS treats apps run from Downloads with more suspicion, and “Launch at login” won’t stick reliably from there.</span>
            </li>
          </ol>
        </section>

        <section className="product-install">
          <h2 className="product-section-title">2 · First launch: the Gatekeeper step</h2>
          <p className="product-section-sub">
            If you just double-click, macOS refuses with <em>“Apple could not verify &quot;SeaCloud
            Mute&quot; is free of malware that may harm your Mac or compromise your privacy.”</em>
            — and the only buttons are <strong>Move to Trash</strong> and <strong>Done</strong>.
            That is expected and it is not a malfunction: it means the app isn’t yet notarized
            through Apple’s paid Developer Program. There is no <em>Open</em> button in that dialog,
            which is why the next step goes through System Settings. Here is how to get past it,
            once:
          </p>
          <ol className="product-steps">
            <li>
              <span className="product-step-num">1</span>
              <span>Double-click SeaCloud Mute in Applications. macOS refuses — expected, once.</span>
            </li>
            <li>
              <span className="product-step-num">2</span>
              <span>
                Open <strong>System Settings → Privacy &amp; Security</strong> and scroll down to the
                <strong> Security</strong> section. A line about SeaCloud Mute being blocked is
                waiting there, with an <strong>Open Anyway</strong> button.
              </span>
            </li>
            <li>
              <span className="product-step-num">3</span>
              <span>
                Click <strong>Open Anyway</strong> and confirm with Touch ID or your password.
                macOS opens SeaCloud Mute straight away — if it doesn’t, double-click it once more.
                Every launch after this is an ordinary double-click.
              </span>
            </li>
          </ol>
          <div className="product-note">
            <Info size={16} strokeWidth={2} />
            <span>
              No <strong>Open Anyway</strong> button in Privacy &amp; Security? It only appears once
              macOS has actually blocked a launch — try opening the app again, then look. On
              <strong> macOS 14 Sonoma</strong> you can skip System Settings entirely: right-click
              (or Control-click) the app and choose <strong>Open</strong>. Apple removed that
              shortcut in macOS 15 Sequoia, which is why the route above is the one that works on
              every version.
            </span>
          </div>
          <div className="product-note">
            <Info size={16} strokeWidth={2} />
            <span>
              This isn’t a workaround for a security check — it <em>is</em> the check. macOS treats
              it as you vouching for the app deliberately, rather than opening something by
              accident. We plan to move to a notarized build, at which point this step disappears.
            </span>
          </div>
        </section>

        <section className="product-install">
          <h2 className="product-section-title">3 · Permissions it asks for</h2>
          <p className="product-section-sub">
            Which prompt you see depends on your headphones. You will not see both.
          </p>
          <ol className="product-steps">
            <li>
              <span className="product-step-num">BT</span>
              <span>
                <strong>Bluetooth</strong> — for Sony headphones. Needed to read the battery level
                and to notice when you press the noise-cancelling button. macOS reports no battery
                at all for Sony headsets, so this is the only way to show it. Click <strong>Allow</strong>.
              </span>
            </li>
            <li>
              <span className="product-step-num">Mic</span>
              <span>
                <strong>Microphone</strong> — for AirPods. macOS only routes the stem press to an
                app that is listening, so this is what makes the gesture reach us. The audio is
                discarded immediately and never leaves your Mac. Click <strong>OK</strong>.
              </span>
            </li>
          </ol>
          <div className="product-note">
            <AlertTriangle size={16} strokeWidth={2} />
            <span>
              Declined by accident? Re-enable it under <strong>System Settings › Privacy &amp;
              Security</strong>, then quit and reopen SeaCloud Mute.
            </span>
          </div>
        </section>

        <section className="product-install">
          <h2 className="product-section-title">4 · Find it in the menu bar</h2>
          <p className="product-section-sub">
            There is no Dock icon and no window — that is deliberate. Look for the microphone icon
            near the clock. Click it to mute, or to open the panel with battery, noise-cancelling
            controls and Settings.
          </p>
          <div className="product-note">
            <Info size={16} strokeWidth={2} />
            <span>
              Can’t see it? If you use a menu bar manager such as Bartender, Ice or Hidden Bar, it
              may have tucked the icon away — check the hidden section.
            </span>
          </div>
        </section>

        <section className="product-install">
          <h2 className="product-section-title">5 · The question about usage statistics</h2>
          <p className="product-section-sub">
            About a minute after first launch, SeaCloud Mute asks whether it may send anonymous
            usage numbers. It is a genuine question — <strong>Not now</strong> is a real answer, the
            app works identically either way, and nothing is sent unless you agree.
          </p>
          <img
            src="/images/seacloud-mute/usage-consent.png"
            alt="The SeaCloud Mute usage statistics dialog, listing what is sent daily and what is never sent, with Not now and Send usage stats buttons"
            className="product-screenshot"
          />
          <p className="product-section-sub">
            Why we ask at all: this is free software built in spare time, and real usage numbers
            decide whether it keeps being developed — and whether we pay for Apple’s Developer
            Program so the Gatekeeper step above goes away for everyone. Nothing identifying is
            ever collected: no account, no ID, no way to follow a machine from one day to the next.
            You can change your answer any time in <strong>Settings › Privacy</strong>.
          </p>
        </section>

        <section className="product-install">
          <h2 className="product-section-title">6 · It starts automatically</h2>
          <p className="product-section-sub">
            SeaCloud Mute adds itself to your login items the first time it runs, so it is there
            when your next call starts — a mute button that isn’t running can’t mute
            anything. We do this once and never again: turn it off under
            <strong> Settings › Launch at login</strong> and it stays off.
          </p>
        </section>

        <section className="product-requirements">
          <h2 className="product-section-title">If something isn’t working</h2>
          <ul className="product-req-list">
            <li><strong>“Apple could not verify … is free of malware”</strong> — expected on first launch, and the dialog has no Open button. Approve it once in System Settings › Privacy &amp; Security › Security › <strong>Open Anyway</strong> (step 2). Do not click Move to Trash.</li>
            <li><strong>No icon in the menu bar</strong> — the app has no Dock icon by design. Check whether a menu bar manager has hidden it.</li>
            <li><strong>Sony battery not showing</strong> — Bluetooth permission was declined. Re-enable it in System Settings › Privacy &amp; Security › Bluetooth, then restart the app.</li>
            <li><strong>AirPods stem press does nothing</strong> — turn on “AirPods tap control” in Settings, and make sure Microphone permission is granted. It only works during a call.</li>
            <li><strong>Sony button changes noise cancelling instead of muting</strong> — check “NC button mutes the mic” is on in Settings.</li>
            <li><strong>Why is the AirPods gesture off by default?</strong> To receive a stem press,
            macOS requires the app to hold a voice-processing session — and that session slightly
            ducks your other audio and stacks a second echo canceller on top of your call app’s
            own. On some setups that made calls audibly worse, so we leave the choice to you. Sony
            headphones need none of this, which is why their button works out of the box. Turn on
            “AirPods tap control” in Settings if you want the stem press.</li>
          </ul>
        </section>

        <section className="product-footer-cta">
          <p>
            Still stuck?{' '}
            <a href="mailto:hello@seacloud-software.hr" className="legal-link">hello@seacloud-software.hr</a>
            {' '}— tell us what you saw and we’ll fix the guide.
          </p>
        </section>
      </div>
    </div>
  );
}
